import { useMsal } from '@azure/msal-react';
import { useAppContext } from '@/store/appContext';
import { loginRequest } from '@/auth/authConfig';
import { logAuthEvent } from '@/graph/auditMiddleware';

/**
 * All auth flows use redirect (not popup) because redirect is the only
 * option that works reliably across PWA / mobile / Safari / corporate browsers.
 * The tradeoff (full page reload) is worth the stability gain.
 */

export function useAuth() {
  const { instance } = useMsal();
  const { currentUser, isInitialized, graphClient } = useAppContext();

  const logout = async () => {
    // Log the event to SharePoint BEFORE the redirect wipes the session
    try {
      if (graphClient && currentUser) {
        await logAuthEvent(graphClient, 'Logout', currentUser.email);
      }
    } catch {
      /* swallow */
    }
    // Clear only our own session markers; let MSAL manage its own localStorage
    Object.keys(sessionStorage)
      .filter((k) => k.startsWith('prism_'))
      .forEach((k) => sessionStorage.removeItem(k));

    try {
      await instance.logoutRedirect();
    } catch (err) {
      console.error('[Prism] logoutRedirect failed:', err);
    }
  };

  const login = async () => {
    try {
      await instance.loginRedirect(loginRequest);
    } catch (err) {
      console.error('[Prism] loginRedirect failed:', err);
    }
  };

  return {
    user: currentUser,
    role: currentUser?.role ?? 'Member',
    isAuthenticated: !!currentUser,
    isLoading: !isInitialized,
    login,
    logout,
  };
}
